import React from 'react';
import styled from 'styled-components';
import { components } from 'react-select';

import {
  DefaultSelect,
  setSelectThemeColor,
} from '../../../components/common/atom/Select';
import { Text } from '../../../components/common/atom/Text';
import Icon from '../../../components/common/atom/Icon';
import { Flex } from '../../../components/common/atom/Layout';
import { BulletList } from '../../../components/common/molecules/BulletList';

import TEXT from '../../../lib/text/textSalesBabyface.json';
import ThemeStyle from '../../../lib/style/ThemeStyle';

const BabyfaceSalesOptions = ({
  babyfaceDefaultPrice,
  babyfacePrice,
  fastAnalysisDiscount = 0,
  activeCampaigns,
  salesOptions,
  conversionOption,
  printOption,
  additionalOption,
  doSelectConversionOption,
  doSelectPrintOption,
  doSelectAdditionalOption,
  doDeleteConversionOption,
  doDeletePrintOption,
  doDeleteAdditionalOption,
}) => {
  const doGetOptions = (optionName) => {
    return salesOptions.filter((item) => item.name === optionName)[0].options;
  };

  const WarningNotice = ({ children }) => {
    return (
      <Flex padding="0.313rem 0 0 0.313rem" justifyContent="flex-start">
        <Icon iconName="info" size={14} style={{ color: '#ff6452' }} />
        <Text size={14} style={{ margin: '2px 0 0 2px' }} color="orangePink">
          {children}
        </Text>
      </Flex>
    );
  };

  const Option = (props) => {
    const { name, id, price } = props.data;

    return (
      <components.Option {...props}>
        <OptionBox>
          <OptionName>{name}</OptionName>
          <OptionPrice>
            +{price.toLocaleString()}
            {textMonetaryUnit}
          </OptionPrice>
        </OptionBox>
        {additionalOption.map((item) => item.id).includes(id) && (
          <WarningNotice>{textLimitedAdditionalItem}</WarningNotice>
        )}
      </components.Option>
    );
  };

  const Description = ({ optionName, clickEvent, description, price }) => {
    const isConversionOption = doGetOptions('필수옵션').map((item) => item.name).includes(optionName);
    const originalPrice = (isConversionOption ? babyfaceDefaultPrice : 0) + price;
    
    // 빠른 분석 옵션인지 확인 (현재 선택된 옵션이 빠른 분석인지)
    const isCurrentFastOption = conversionOption && (
      conversionOption.name.includes('빠른') || 
      conversionOption.name.includes('24시간') ||
      conversionOption.name.toLowerCase().includes('fast')
    ) && optionName === conversionOption.name;
    
    // 빠른 분석 할인 적용 (빠른 분석 옵션 선택 시)
    // 빠른 분석 옵션의 가격(4500원)과 빠른 분석 할인(4500원)이 상쇄되므로
    // babyfacePrice에서 빠른 분석 할인만 적용 (옵션 가격은 더하지 않음)
    let finalPrice;
    let babyfacePriceWithDiscount;
    
    if (isConversionOption) {
      // 필수 옵션(변환 옵션)인 경우
      if (isCurrentFastOption && fastAnalysisDiscount > 0) {
        // 빠른 분석 옵션이면 할인만 적용 (옵션 가격은 더하지 않음)
        babyfacePriceWithDiscount = babyfacePrice - fastAnalysisDiscount;
        finalPrice = babyfacePriceWithDiscount;
      } else {
        // 일반 옵션이면 babyfacePrice + 옵션 가격
        babyfacePriceWithDiscount = babyfacePrice;
        finalPrice = babyfacePrice + price;
      }
    } else {
      // 인화 옵션이나 추가 옵션인 경우 옵션 가격만 표시
      babyfacePriceWithDiscount = babyfacePrice;
      finalPrice = price;
    }
    
    console.log('Description 가격 계산:', {
      optionName,
      isConversionOption,
      isCurrentFastOption,
      babyfacePrice,
      babyfacePriceWithDiscount,
      price,
      fastAnalysisDiscount,
      finalPrice,
      originalPrice
    });

    return (
      <DescriptionBox>
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Text size={14} color="black">
            {optionName}
          </Text>
          <Icon
            iconName="close"
            size="20"
            style={{ color: ThemeStyle.color.black }}
            onClick={clickEvent}
          />
        </Flex>
        <BulletList
          contents={description.split(';')}
          ulStyle={{
            marginBlockStart: '0.625rem',
            marginBlockEnd: '0.625rem',
            lineHeight: '1.5',
            color: '#2e2e2e',
          }}
          liStyle={{ fontSize: '0.75rem' }}
        />
        { isConversionOption && activeCampaigns.length > 0 &&
          <Flex column alignItems="flex-end" style={{ marginTop: '0.5rem' }}>
            {activeCampaigns.map((campaign, idx) => (
              <Flex key={idx} justifyContent="flex-end" style={{ marginBottom: idx < activeCampaigns.length - 1 ? '0.25rem' : '0' }}>
                <Text
                  size={11}
                  color="black"
                >
                  {campaign.description}
                </Text>
              </Flex>
            ))}
            <Text
              size={14}
              color="black"
              style={{ 
                textDecoration: 'line-through',
                marginTop: '0.25rem'
              }}
            >
              {originalPrice.toLocaleString()}
              {textMonetaryUnit}
            </Text>
          </Flex>
        }
        <Text
          size={14}
          color="black"
          textAlign="end"
          style={{ display: 'block' }}
        >
          +{finalPrice.toLocaleString()}
          {textMonetaryUnit}
        </Text>
      </DescriptionBox>
    );
  };

  return (
    <>
      <DefaultSelect
        margin="1rem 0 0 0"
        fontSize="0.875rem"
        placeholder={placeholderConversionItem}
        value={conversionOption}
        options={doGetOptions('필수옵션').filter(el => !el.name.includes('인화'))}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        components={{ Option }}
        onChange={(e) => doSelectConversionOption(e)}
        isSearchable={false}
        styles={SELECT_STYLE}
        theme={(theme) => setSelectThemeColor(theme, SELECT_THEME_COLORS)}
      />
      {conversionOption === null && (printOption !== null || additionalOption.length > 0) && (
        <WarningNotice>{textRequiredInfoConversionItem}</WarningNotice>
      )}
      <DefaultSelect
        margin="1rem 0 0 0"
        fontSize="0.875rem"
        placeholder={placeholderPrintItem}
        value={printOption}
        options={doGetOptions('인화옵션')}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        components={{ Option }}
        onChange={(e) => doSelectPrintOption(e)}
        isSearchable={false}
        styles={SELECT_STYLE}
        theme={(theme) => setSelectThemeColor(theme, SELECT_THEME_COLORS)}
      />
      {printOption === null && (conversionOption !== null || additionalOption.length > 0) && (
        <WarningNotice>{textRequiredInfoPrintItem}</WarningNotice>
      )}
      <DefaultSelect
        margin="1.25rem 0 0 0"
        fontSize="0.875rem"
        placeholder={placeholderAdditionalItem}
        value={
          additionalOption.length > 0
            ? additionalOption[additionalOption.length - 1]
            : additionalOption
        }
        options={doGetOptions('추가옵션')}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        isOptionDisabled={(option) =>
          additionalOption.map((item) => item.id).includes(option.id)
        }
        components={{ Option }}
        onChange={(e) => doSelectAdditionalOption(e)}
        isSearchable={false}
        styles={SELECT_STYLE}
        theme={(theme) => setSelectThemeColor(theme, SELECT_THEME_COLORS)}
      />
      {conversionOption && (
        <Description
          optionName={conversionOption.name}
          clickEvent={() => doDeleteConversionOption()}
          description={conversionOption.description}
          price={conversionOption.price}
        />
      )}
      {printOption && (
        <Description
          optionName={printOption.name}
          clickEvent={() => doDeletePrintOption()}
          description={printOption.description}
          price={printOption.price}
        />
      )}
      {additionalOption.length > 0 &&
        additionalOption.map((option, idx) => {
          const { name, id, price, description } = option;
          return (
            <React.Fragment key={idx}>
              <Description
                optionName={name}
                clickEvent={() => doDeleteAdditionalOption({ id, price })}
                description={description}
                price={price}
              />
            </React.Fragment>
          );
        })}
    </>
  );
};

export default BabyfaceSalesOptions;

const {
  textMonetaryUnit,
  textLimitedAdditionalItem,
  placeholderConversionItem,
  textRequiredInfoConversionItem,
  placeholderPrintItem,
  textRequiredInfoPrintItem,
  placeholderAdditionalItem,
} = TEXT;

const SELECT_STYLE = {
  control: (provided) => ({
    ...provided,
    padding: '5px 5px 3px',
    boxShadow: 'none',
    borderRadius: '6px',
    borderColor: '#929292',
    color: '#231815',
    fontSize: '1rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#231815',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    width: '0',
  }),
  option: (provided, { isSelected, isDisabled }) => ({
    ...provided,
    padding: '10px 20px',
    borderBottom: '1px solid #c7c7c7',
    backgroundColor: isDisabled
      ? '#e7e7e7'
      : isSelected
      ? 'rgba(255, 100, 82, 0.1)'
      : '#ffffff',
    color: isDisabled ? '#929292' : '#231815',
    ':first-of-type': {
      ...provided[':first-of-type'],
      paddingTop: '12px',
      borderRadius: '4px 4px 0 0',
    },
    ':last-child': {
      ...provided[':last-child'],
      borderBottom: 'none',
      borderRadius: '0 0 4px 4px',
    },
  }),
  menu: (provided) => ({
    ...provided,
    boxShadow: 'none',
    border: '1px solid #929292',
    borderRadius: '6px',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: '0',
    paddingBottom: '0',
  }),
};

const SELECT_THEME_COLORS = {
  primary: 'rgba(255, 100, 82, 0.7)',
  primary50: 'rgba(255, 100, 82, 0.1)',
};

const OptionBox = styled.div`
  display: flex;
  font-size: 1rem;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
`;

const OptionName = styled.span``;

const OptionPrice = styled.span`
  margin-left: auto;
  letter-spacing: 0.3px;
`;

const DescriptionBox = styled.div`
  padding: 1.25rem 0.813rem;
  margin: 1.25rem 0 0;
  border: 1px solid #c7c7c7;
  border-radius: 0.375rem;
  background: #f6f6f6;
  width: 100%;
`;
